/**
 * App-level Query manager (ADR-0047) — a builder rail BOTTOM-zone area (the
 * app-global zone, next to Stato globale / Librerie JS / Libreria componenti),
 * where the operator AUTHORS the app's server primitives: a **Query**
 * (table/dataset/http passthrough), a **Compute** (read-only JS), a **Logic**
 * (server JS that writes + validates), or a constant. Each can be invoked from a
 * page (`source_kind: "query"`), run on a SCHEDULE, and/or exposed as a REST API
 * — with a live, auto-generated API reference (a "Documentazione API" list row).
 *
 * This is the editor-centric home the owner asked for ("la 'logic' è solo un
 * kind … lo facciamo nell'editor", then "un'icona nella rail in basso come le
 * altre globali"). It follows the WS-7/WS-8 rail-bottom MASTER/DETAIL pattern
 * (mirror of `CustomComponentsPane`/`JsLibrariesPane`): a `view` prop
 * (`"list"|"detail"|"full"`) with CONTROLLED selection lifted to `PageBuilderPage`,
 * so it mounts TWICE — the LIST in the lateral sidebar, the DETAIL in the content.
 * Create is IMMEDIATE server-side (no unsaved draft): "＋ Nuova" creates a default
 * Logic Query and selects it. The `["apps", appId, "queries"]` TanStack query is
 * SHARED by cache with `PageBuilderPage`'s `QueryForm` "Query app" picker, so a
 * create/delete here repopulates that picker.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bug, ChevronRight, FileText, Play, Plus, Server, Trash2 } from "lucide-react";
import * as React from "react";

import { ApiError } from "@/lib/api";
import {
  appsApi,
  type AppQueryOut,
  type AppQuerySourceKind,
  type AppQueryTrigger,
  type AppQueryUpsert,
  type QueryParam,
  type QueryParamLocation,
  type QueryParamType,
} from "@/lib/api-apps";

import { looksLikeHandler } from "@/lib/appLogicTs";
import type { WireNode } from "@/lib/filterTree";

import { CodeField } from "./CodeField";
import { FilterTreeBuilder } from "./FilterTreeBuilder";
import { QueryDebugPanel } from "./QueryDebugPanel";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

/** Ambient TS for the Logic/Compute Monaco — the write-capable QuickJS `ctx`.
 *  DYNAMIC + GENERIC: a `TableColumns` map binds each table NAME to a union of
 *  its column names, and `ctx.db.query<T>(table, …)` narrows `filters[].column`
 *  to that table's columns — so `query('deals', { filters: [{ column: '` REALLY
 *  autocompletes the columns (not just buffer words). The owner's "intellisense
 *  forte sulle colonne". */
const _SYS_COLS = ["id", "created_at", "updated_at"];

const _OPS_DTS = `"eq" | "ne" | "lt" | "lte" | "gt" | "gte" | "in" | "contains" | "is_null" | "not_null" | "has"`;

function buildLogicDts(
  tables: { id: string; name: string }[],
  columnsByTable: Record<string, string[]>,
): string {
  const colUnion = (cols: string[]): string => {
    if (!cols.length) return "string"; // unknown columns → stay permissive
    const all = Array.from(new Set([...cols, ..._SYS_COLS]));
    return all.map((c) => JSON.stringify(c)).join(" | ");
  };

  const rowName = (t: { name: string }) => `Row_${t.name.replace(/[^A-Za-z0-9_]/g, "_")}`;
  const rowIface = (t: { id: string; name: string }): string => {
    const cols = Array.from(new Set([...(columnsByTable[t.id] ?? []), ..._SYS_COLS]));
    const fields = cols.map((c) => `  ${JSON.stringify(c)}: any;`).join("\n");
    return `interface ${rowName(t)} {\n${fields}\n}`;
  };

  // No tables yet → a simple, non-generic shape (column/table = string, row = any).
  // With tables → each table gets a typed ROW interface + a `RowOf<T>` mapping, so
  // `ctx.db.query('deals').rows[0].` autocompletes the real columns (not `any[]`).
  const tableMap = tables.length
    ? [
        `interface TableColumns {\n${tables
          .map((t) => `  ${JSON.stringify(t.name)}: ${colUnion(columnsByTable[t.id] ?? [])};`)
          .join("\n")}\n}\ntype TrayTableName = keyof TableColumns;`,
        tables.map(rowIface).join("\n"),
        `type RowOf<T extends TrayTableName> =\n${tables
          .map((t) => `  T extends ${JSON.stringify(t.name)} ? ${rowName(t)} :`)
          .join("\n")}\n  Record<string, any>;`,
      ].join("\n")
    : `type TrayTableName = string;\ntype TableColumns = Record<string, string>;\ntype RowOf<T extends TrayTableName = TrayTableName> = Record<string, any>;`;

  return `
/** Il risultato di una lettura: righe tipizzate sulla tabella + totale. */
interface TrayQueryResult<T extends TrayTableName = TrayTableName> { rows: RowOf<T>[]; total: number; data: any; }
${tableMap}
/** Il valore di un filtro: uno scalare (o un array per 'in'/'has'). NON un oggetto —
 *  passa il singolo campo (input.deal_id), non l'intero input. */
type TrayFilterValue = string | number | boolean | null | (string | number | boolean)[];
/** Un filtro WHERE: { column, op, value }. op 'in' vuole un array; 'is_null' /
 *  'not_null' ignorano value; 'has' = id in un array relation_many. */
interface TrayFilter<C extends string = string> { column: C; op: ${_OPS_DTS}; value?: TrayFilterValue; }
/** Un nodo dell'albero booleano (ADR-0055): una FOGLIA { column, op, value } oppure un
 *  connettivo { and: [...] } / { or: [...] } / { not: ... }. Una lista di filtri = AND. */
type TrayFilterNode<C extends string = string> =
  | TrayFilter<C>
  | { and: TrayFilterNode<C>[] }
  | { or: TrayFilterNode<C>[] }
  | { not: TrayFilterNode<C> };
interface TraySort<C extends string = string> { column: C; descending?: boolean; }
/** Opzioni di lettura. \`filters\` = una LISTA di nodi (AND implicito) OPPURE un singolo
 *  nodo/albero \`{and/or/not}\`; \`filter\` = alias per un singolo albero. Il runtime accetta
 *  entrambi (\`opts.filter or opts.filters\`), quindi qui sono equivalenti. */
interface TrayQueryOpts<C extends string = string> { filters?: TrayFilterNode<C>[] | TrayFilterNode<C>; filter?: TrayFilterNode<C>; sort?: TraySort<C>[]; search?: string; page?: number; page_size?: number; }
type ColsOf<T extends TrayTableName> = Extract<TableColumns[T], string>;
interface TrayDb {
  /** Legge le righe di una tabella (per nome). DUE forme, entrambe con intellisense:
   *  • STRUTTURATA — \`ctx.db.query('deals', { filters: [{ column: 'stage', op: 'eq',
   *    value: input.s }] })\`: \`column\` autocompleta le colonne reali; \`filters\` accetta
   *    anche { and/or/not } (albero booleano). \`rows[0].\` autocompleta le colonne.
   *  • PREDICATO (ADR-0055) — \`ctx.db.query('deals', d => d.stage === 'won' &&
   *    d.amount >= input.min)\`: la freccia NON viene eseguita, al salvataggio è tradotta
   *    in un filtro. Usa === > >= < <= !==, &&/||/!, d.colonna, d.rel.campo.
   *  Una sola firma con parametro a UNIONE così TS tipizza l'oggetto contro le opzioni
   *  e la freccia contro la riga (niente trappola dell'ordine degli overload). */
  query<T extends TrayTableName>(
    table: T,
    optsOrPredicate?: TrayQueryOpts<ColsOf<T>> | ((row: RowOf<T>) => boolean),
  ): TrayQueryResult<T>;
  /** Inserisce una riga; values = colonne della tabella. Ritorna la riga creata. */
  create<T extends TrayTableName>(table: T, values: Partial<Record<ColsOf<T>, any>>): RowOf<T>;
  /** Aggiorna una riga per id; ritorna la riga (null se non corrisponde). */
  update<T extends TrayTableName>(table: T, rowId: string, values: Partial<Record<ColsOf<T>, any>>): RowOf<T> | null;
  /** Cancella (soft-delete) una riga per id. */
  delete(table: TrayTableName, rowId: string): boolean;
}
interface TrayCtx {
  db: TrayDb;
  /** I parametri della chiamata (REST / pulsante / {} per uno schedule). */
  input: Record<string, any>;
  /** L'utente invocante (null per uno schedule di SISTEMA). */
  user: any;
  now(): string;
  /** Annulla l'operazione: rollback + 422 con il messaggio. */
  fail(message: string): never;
}
declare const ctx: TrayCtx;
`;
}

const PARAM_TYPES: QueryParamType[] = ["string", "number", "boolean", "object", "array"];
const PARAM_LOCATIONS: QueryParamLocation[] = ["query", "path", "body"];
const _PARAM_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** Build the `ctx.input` payload from a declared contract + the guided-form
 *  string values, coercing per param type (mirrors the backend's `_type_ok`).
 *  An empty non-required value is OMITTED (the server treats it as absent). */
function buildInputFromValues(
  params: QueryParam[],
  values: Record<string, string>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const p of params) {
    const raw = (values[p.name] ?? "").trim();
    if (p.type === "boolean") {
      out[p.name] = values[p.name] === "true";
      continue;
    }
    if (!raw) {
      if (p.required) out[p.name] = null; // let the server 422 the missing required
      continue;
    }
    if (p.type === "number") out[p.name] = Number(raw);
    else if (p.type === "object" || p.type === "array") {
      try {
        out[p.name] = JSON.parse(raw);
      } catch {
        out[p.name] = raw; // let the server 422 on a bad shape
      }
    } else out[p.name] = raw;
  }
  return out;
}

/** The input the test-run / debugger uses: the guided contract form when a contract is
 *  declared, else the raw JSON box (non-throwing — a bad JSON just debugs with `{}`). */
function resolveRunInput(
  schema: QueryParam[],
  runValues: Record<string, string>,
  runInput: string,
): Record<string, unknown> {
  if (schema.length > 0) return buildInputFromValues(schema, runValues);
  const raw = runInput.trim();
  if (raw && raw !== "{}") {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

const KIND_OPTIONS: { value: AppQuerySourceKind; label: string }[] = [
  { value: "table", label: "Tabella" },
  { value: "dataset", label: "Proiezione" },
  { value: "http", label: "HTTP" },
  { value: "js", label: "Compute" },
  { value: "logic", label: "Logic" },
  { value: "static", label: "Statico" },
];

const KIND_BADGE: Record<AppQuerySourceKind, string> = {
  table: "Tab",
  dataset: "Proi",
  http: "HTTP",
  js: "Compute",
  logic: "Logic",
  static: "Const",
};

// NB REST exposure (the ApiContract + run-as-role) moved OFF the Query onto the
// app's REST INTERFACE (ADR-0051, the single exposure registry) — a Query is
// exposed by adding it as an endpoint there, not by a per-Query flag here.
interface Draft {
  key: string;
  name: string;
  description: string;
  source_kind: AppQuerySourceKind;
  trigger: AppQueryTrigger;
  spec: Record<string, unknown>;
  /** Declared input contract (ADR-0054) — validated server-side on run → 422. */
  input_schema: QueryParam[];
  /** TS source + extracted output (ADR-0054 S2) — carried through so a save from
   *  the current editor never wipes them; the TS pipeline (S2c) populates them. */
  code_ts: string | null;
  output_schema: Record<string, unknown> | null;
  schedule_cron: string;
  enabled: boolean;
}

function draftFromQuery(q: AppQueryOut): Draft {
  return {
    key: q.key,
    name: q.name,
    description: q.description ?? "",
    source_kind: q.source_kind,
    trigger: q.trigger,
    spec: { ...(q.spec ?? {}) },
    input_schema: (q.input_schema ?? []).map((p) => ({ ...p })),
    code_ts: q.code_ts ?? null,
    output_schema: q.output_schema ?? null,
    schedule_cron: q.schedule_cron ?? "",
    enabled: q.enabled,
  };
}

function upsertFromDraft(d: Draft): AppQueryUpsert {
  return {
    key: d.key.trim(),
    name: d.name.trim(),
    description: d.description.trim() || null,
    source_kind: d.source_kind,
    trigger: d.trigger,
    spec: d.spec,
    input_schema: d.input_schema,
    code_ts: d.code_ts,
    output_schema: d.output_schema,
    schedule_cron: d.trigger === "schedule" ? d.schedule_cron.trim() || null : null,
    enabled: d.enabled,
  };
}

/** A default new Query — a Logic with a tiny starter body (so the create passes
 *  the `logic_needs_code` validation; the operator edits it after). */
function newQueryBody(existing: AppQueryOut[]): AppQueryUpsert {
  const usedKeys = new Set(existing.map((q) => q.key));
  let n = existing.length + 1;
  while (usedKeys.has(`q${n}`)) n += 1;
  return {
    key: `q${n}`,
    name: `Query ${n}`,
    source_kind: "logic",
    trigger: "manual",
    // ADR-0054: a typed `handler(ctx, input): Output` skeleton — Monaco runs real TS
    // type-checking on it; on save it's transpiled + bridged to the raw-body runtime.
    spec: { code: _LOGIC_SKELETON },
    code_ts: _LOGIC_SKELETON,
    schedule_cron: null,
    enabled: true,
  };
}

const _LOGIC_SKELETON = `interface Input {
  // deal_id: string;
}
interface Output {
  ok: boolean;
}
function handler(ctx: TrayCtx, input: Input): Output {
  // NB: passa il SINGOLO campo come value (input.deal_id), non l'intero input.
  // const d = ctx.db.query('deals', { filters: [{ column: 'id', op: 'eq', value: input.deal_id }] }).rows[0];
  // if (!d) ctx.fail('non trovato');
  return { ok: true };
}`;

// Base field styling WITHOUT a width (so a caller can set `w-24` / `flex-1`
// without a Tailwind width conflict — that bug squashed the path input). The
// full-width variant is the default for stacked label+field rows.
const fieldCls = "rounded border border-border bg-background px-1.5 py-1 text-[12px]";
const inputCls = `w-full ${fieldCls}`;
const labelCls = "text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70";

type AreaView = "list" | "detail" | "full";

export function AppQueriesPane({
  appId,
  tables = [],
  datasets = [],
  columnsByTable = {},
  canWrite = true,
  view = "full",
  selectedId: selectedIdProp,
  onSelectId,
}: {
  appId: string;
  tables?: { id: string; name: string }[];
  datasets?: { id: string; name: string }[];
  /** Per-table column NAMES (table id → names) — feeds the Logic/Compute `ctx`
   *  intellisense so the author sees a table's fields on hover. */
  columnsByTable?: Record<string, string[]>;
  canWrite?: boolean;
  view?: AreaView;
  /** Controlled selection (master/detail split). A query id, `API_REF_ID`, or
   *  null. Omit for the legacy single pane. */
  selectedId?: string | null;
  onSelectId?: (id: string | null) => void;
}) {
  const qc = useQueryClient();
  const queriesQ = useQuery({
    queryKey: ["apps", appId, "queries"],
    queryFn: () => appsApi.listQueries(appId),
  });
  const queries = React.useMemo(() => queriesQ.data ?? [], [queriesQ.data]);
  // Pages — to label the page-owned (`page_id`) Query groups (ADR-0048 convergence).
  // Cache-shared with the builder's own pages query (same key).
  const pagesQ = useQuery({
    queryKey: ["apps", appId, "pages"],
    queryFn: () => appsApi.listPages(appId),
  });
  const pageTitle = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const p of pagesQ.data ?? []) m.set(p.id, p.title || p.slug || p.id);
    return m;
  }, [pagesQ.data]);

  // The `ctx` intellisense DTS — rebuilt only when the table names/columns change
  // (keyed on a string so a fresh `tables` array ref per render doesn't churn it).
  const tablesKey = tables.map((t) => `${t.id}:${t.name}`).join(",");
  const colsKey = JSON.stringify(columnsByTable);
  const logicDts = React.useMemo(
    () => buildLogicDts(tables, columnsByTable),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tablesKey, colsKey],
  );

  const controlled = onSelectId !== undefined;
  const [internalId, setInternalId] = React.useState<string | null>(null);
  const selectedId = controlled ? selectedIdProp ?? null : internalId;
  const setSelectedId = (id: string | null) => (controlled ? onSelectId!(id) : setInternalId(id));

  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [runResult, setRunResult] = React.useState<string | null>(null);
  const [runInput, setRunInput] = React.useState("{}");
  // Guided test-run: one value per declared param (used when the Query has a
  // declared input contract; otherwise the raw JSON box drives the run).
  const [runValues, setRunValues] = React.useState<Record<string, string>>({});
  // Live count of TS ERROR markers from Monaco (ADR-0054) — blocks the save when the
  // handler body has type errors. Reset when the selection changes.
  const [tsErrors, setTsErrors] = React.useState(0);
  // Whether the code Query's Debug panel is open — lifted here so the right rail
  // (Contratto/Prova, redundant with the debugger's own Input box) can be hidden.
  const [debugging, setDebugging] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const visible = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return queries;
    return queries.filter((x) => x.name.toLowerCase().includes(q) || x.key.toLowerCase().includes(q));
  }, [queries, search]);

  // Group the list by SCOPE (ADR-0048): app-level Queries (`page_id` null) first,
  // then one section per owning page (a page's extracted Queries nest under it).
  const groups = React.useMemo(() => {
    const appLevel: AppQueryOut[] = [];
    const byPage = new Map<string, AppQueryOut[]>();
    for (const q of visible) {
      if (!q.page_id) appLevel.push(q);
      else (byPage.get(q.page_id) ?? byPage.set(q.page_id, []).get(q.page_id)!).push(q);
    }
    const pageGroups = [...byPage.entries()]
      .map(([pid, qs]) => ({ id: pid, label: pageTitle.get(pid) ?? "Pagina", queries: qs }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { appLevel, pageGroups };
  }, [visible, pageTitle]);

  // Seed the editor draft when the selection changes (mirror CustomComponentsPane).
  const selected = selectedId ? queries.find((q) => q.id === selectedId) ?? null : null;
  React.useEffect(() => {
    if (selected && (!draft || draft.key !== selected.key || selectedId !== selected.id)) {
      setDraft(draftFromQuery(selected));
      setRunResult(null);
      setRunValues({});
      setTsErrors(0);
      setError(null);
      setDebugging(false); // close the debugger when switching queries
    }
    if (!selected) setDraft(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, selectedId]);

  const patch = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const patchSpec = (p: Record<string, unknown>) =>
    setDraft((d) => (d ? { ...d, spec: { ...d.spec, ...p } } : d));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["apps", appId, "queries"] });
  };

  const createMut = useMutation({
    mutationFn: () => appsApi.createQuery(appId, newQueryBody(queries)),
    onSuccess: (saved) => {
      invalidate();
      setError(null);
      setSelectedId(saved.id);
      setDraft(draftFromQuery(saved));
    },
    onError: (e) => setError(errMsg(e)),
  });

  const saveMut = useMutation({
    mutationFn: (body: AppQueryUpsert) => appsApi.updateQuery(appId, selectedId!, body),
    onSuccess: (saved) => {
      invalidate();
      setError(null);
      setDraft(draftFromQuery(saved));
    },
    onError: (e) => setError(errMsg(e)),
  });

  // ADR-0054 S2 — compile a js/logic body (TS source) at save: transpile → `spec.code`
  // (what QuickJS runs, UNCHANGED), block on a TypeScript SYNTAX error, and extract the
  // I/O contract (a declared `interface Input` WINS the manual Contratto; `Output` gives
  // `output_schema`). Non-code kinds clear both TS columns. Returns null to abort the save.
  const [compiling, setCompiling] = React.useState(false);
  const buildSaveBody = async (d: Draft): Promise<AppQueryUpsert | null> => {
    const base = upsertFromDraft(d);
    if (d.source_kind !== "js" && d.source_kind !== "logic") {
      return { ...base, code_ts: null, output_schema: null };
    }
    const src = d.code_ts ?? String(d.spec.code ?? "");
    setCompiling(true);
    try {
      const { compileAppLogic } = await import("@/lib/appLogicTs");
      const res = await compileAppLogic(src);
      if (!res.ok) {
        setError(
          "Errore TypeScript:\n" +
            res.diagnostics.map((x) => `L${x.line ?? "?"}: ${x.message}`).join("\n"),
        );
        return null;
      }
      return {
        ...base,
        spec: { ...(base.spec ?? {}), code: res.codeJs },
        code_ts: src,
        output_schema: (res.contract.output as Record<string, unknown> | null) ?? null,
        input_schema: res.contract.input ?? base.input_schema,
      };
    } catch (e) {
      setError(errMsg(e));
      return null;
    } finally {
      setCompiling(false);
    }
  };
  const handleSave = async () => {
    if (!draft) return;
    setError(null);
    // Block on live Monaco TS type errors (only meaningful for a `handler` body in TS mode).
    const isTs =
      (draft.source_kind === "js" || draft.source_kind === "logic") &&
      looksLikeHandler(draft.code_ts ?? String(draft.spec.code ?? ""));
    if (isTs && tsErrors > 0) {
      setError(`Ci sono ${tsErrors} errori TypeScript nel codice — correggili prima di salvare.`);
      return;
    }
    const body = await buildSaveBody(draft);
    if (body) saveMut.mutate(body);
  };

  const deleteMut = useMutation({
    mutationFn: (id: string) => appsApi.deleteQuery(appId, id),
    onSuccess: () => {
      invalidate();
      setSelectedId(null);
      setDraft(null);
    },
    onError: (e) => setError(errMsg(e)),
  });

  const runMut = useMutation({
    mutationFn: (id: string) => {
      let input: Record<string, unknown> = {};
      // A declared contract drives a guided form; otherwise the raw JSON box.
      if (draft && draft.input_schema.length > 0) {
        input = buildInputFromValues(draft.input_schema, runValues);
      } else {
        const raw = runInput.trim();
        if (raw && raw !== "{}") {
          try {
            input = JSON.parse(raw);
          } catch {
            throw new Error("Input JSON non valido");
          }
        }
      }
      return appsApi.runQuery(appId, id, input);
    },
    onSuccess: (r) => {
      setError(null);
      setRunResult(JSON.stringify(r.result, null, 2));
    },
    onError: (e) => {
      setRunResult(null);
      setError(errMsg(e));
    },
  });

  // --- MASTER (list) ---------------------------------------------------------
  const masterCls =
    view === "full"
      ? "flex w-56 shrink-0 flex-col border-r border-border/60 pr-2"
      : "flex h-full min-h-0 flex-col";

  const renderRow = (q: AppQueryOut) => (
    <button
      key={q.id}
      type="button"
      onClick={() => setSelectedId(q.id)}
      className={`mb-0.5 flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12px] ${
        selectedId === q.id ? "bg-accent/15 text-accent" : "hover:bg-surface"
      }`}
    >
      <Server className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" strokeWidth={1.75} />
      <span className="min-w-0 flex-1 truncate font-medium">{q.name}</span>
      <span className="shrink-0 rounded bg-muted px-1 text-[9px] uppercase text-muted-foreground">
        {KIND_BADGE[q.source_kind]}
      </span>
      {q.trigger === "schedule" && (
        <span className="shrink-0 rounded bg-amber-500/15 px-1 text-[9px] text-amber-600">⏱</span>
      )}
    </button>
  );

  const master = (
    <div className={masterCls}>
      <div className="mb-2 shrink-0 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Query app
      </div>
      {canWrite && (
        <button
          type="button"
          onClick={() => createMut.mutate()}
          disabled={createMut.isPending}
          className="mb-1 flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Nuova query
        </button>
      )}
      {queries.length > 5 && (
        <input
          className="mb-1 rounded border border-border bg-background px-1.5 py-1 text-[11px]"
          placeholder="Cerca…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {queries.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-muted-foreground">Nessuna Query. Creane una con «Nuova».</p>
        ) : visible.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-muted-foreground">Nessun risultato.</p>
        ) : (
          <>
            {groups.appLevel.map(renderRow)}
            {groups.pageGroups.map((g) => (
              <div key={g.id} className="mt-2">
                <div className="mb-0.5 flex items-center gap-1 px-1 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                  <FileText className="h-3 w-3 shrink-0" strokeWidth={1.75} />
                  <span className="min-w-0 truncate">{g.label}</span>
                </div>
                {g.queries.map(renderRow)}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );

  // --- DETAIL ----------------------------------------------------------------
  // Layout: a slim IDENTITY header (name · key · kind · enabled · Salva/Elimina),
  // then a body split into the CENTRAL surface (the code hero for Logic/Compute,
  // the source config otherwise) + a right rail (Contratto · Prova · Impostazioni).
  const detailInner = !draft ? (
    <div className="flex flex-1 items-center justify-center text-[12px] text-muted-foreground">
      Seleziona una Query dalla lista, o creane una nuova.
    </div>
  ) : (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border/60 px-3 py-2">
        <input
          aria-label="Nome query"
          className="min-w-0 max-w-[220px] flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-sm font-semibold text-foreground hover:border-border focus:border-accent focus:outline-none disabled:opacity-70"
          value={draft.name}
          disabled={!canWrite}
          placeholder="Nome query"
          onChange={(e) => patch({ name: e.target.value })}
        />
        <input
          aria-label="Chiave"
          className={`${fieldCls} w-32 min-w-0 font-mono`}
          value={draft.key}
          placeholder="chiave"
          disabled={!canWrite}
          onChange={(e) => patch({ key: e.target.value })}
        />
        <select
          aria-label="Tipo"
          className={`${fieldCls} font-medium`}
          value={draft.source_kind}
          disabled={!canWrite}
          onChange={(e) => patch({ source_kind: e.target.value as AppQuerySourceKind })}
        >
          {KIND_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} disabled={o.value === "dataset" && datasets.length === 0}>
              {o.label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <input type="checkbox" checked={draft.enabled} disabled={!canWrite} onChange={(e) => patch({ enabled: e.target.checked })} />
          Abilitata
        </label>
        <div className="ml-auto flex items-center gap-1.5">
          {canWrite && (
            <button
              type="button"
              disabled={saveMut.isPending || compiling}
              onClick={handleSave}
              className="rounded-md bg-accent px-3 py-1 text-[12px] font-medium text-accent-foreground hover:opacity-90 disabled:opacity-50"
            >
              {compiling ? "Compilazione…" : saveMut.isPending ? "Salvataggio…" : "Salva"}
            </button>
          )}
          {canWrite && selectedId && (
            <button
              type="button"
              title="Elimina"
              aria-label="Elimina"
              onClick={() => {
                if (window.confirm(`Eliminare la Query "${draft.name}"?`)) deleteMut.mutate(selectedId);
              }}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-danger hover:bg-danger/10"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>

      {error && (
        <pre className="mx-3 mt-2 shrink-0 whitespace-pre-wrap break-words rounded border border-danger/40 bg-danger/5 p-2 font-mono text-[10px] text-danger">
          {error}
        </pre>
      )}

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* CENTRAL surface — code hero (Logic/Compute) or the source config. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col p-3">
          <QueryMain
            draft={draft}
            appId={appId}
            tables={tables}
            columnsByTable={columnsByTable}
            datasets={datasets}
            logicDts={logicDts}
            canWrite={canWrite}
            queryId={selectedId ?? ""}
            debugInput={resolveRunInput(draft.input_schema, runValues, runInput)}
            debugging={debugging}
            onDebuggingChange={setDebugging}
            onPatch={patch}
            onPatchSpec={patchSpec}
            onCodeDiagnostics={setTsErrors}
          />
        </div>

        {/* RIGHT RAIL — the input contract, a guided test-run, and settings. Hidden
            while debugging: the debugger has its own Input box + result, so the rail
            would only steal space. */}
        {!debugging && (
          <div className="flex w-full shrink-0 flex-col gap-3 overflow-y-auto border-t border-border/60 p-3 lg:w-[340px] lg:border-l lg:border-t-0">
            <ContractEditor
              params={draft.input_schema}
              canWrite={canWrite}
              onChange={(input_schema) => patch({ input_schema })}
            />

            {selectedId && (
              <TestRunPanel
                params={draft.input_schema}
                runValues={runValues}
                setRunValues={setRunValues}
                runInput={runInput}
                setRunInput={setRunInput}
                runResult={runResult}
                running={runMut.isPending}
                onRun={() => runMut.mutate(selectedId)}
              />
            )}

            <SettingsPanel draft={draft} canWrite={canWrite} onPatch={patch} />
          </div>
        )}
      </div>
    </div>
  );

  if (view === "list") return master;
  if (view === "detail") return <div className="flex h-full min-h-0 flex-col text-[12px]">{detailInner}</div>;

  return (
    <div className="flex h-full min-h-0 gap-3 text-[12px]">
      {master}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-border/60 bg-surface">
        {detailInner}
      </div>
    </div>
  );
}

/** The CENTRAL surface of the editor: the code HERO for Logic/Compute (a
 *  full-height Monaco with impeccable `ctx` intellisense), or the source config
 *  for a table / dataset / http / static Query. Identity (name/key/kind) lives in
 *  the header; the contract + test-run + settings live in the right rail. */
function QueryMain({
  draft,
  appId,
  tables,
  columnsByTable,
  datasets,
  logicDts,
  canWrite,
  queryId,
  debugInput,
  debugging,
  onDebuggingChange,
  onPatch,
  onPatchSpec,
  onCodeDiagnostics,
}: {
  draft: Draft;
  appId: string;
  tables: { id: string; name: string }[];
  columnsByTable: Record<string, string[]>;
  datasets: { id: string; name: string }[];
  logicDts: string;
  canWrite: boolean;
  queryId: string;
  debugInput?: Record<string, unknown>;
  debugging: boolean;
  onDebuggingChange: (v: boolean) => void;
  onPatch: (p: Partial<Draft>) => void;
  onPatchSpec: (p: Record<string, unknown>) => void;
  onCodeDiagnostics?: (errorCount: number) => void;
}) {
  const kind = draft.source_kind;
  const specStr = (k: string) => String((draft.spec[k] as string | undefined) ?? "");
  const codeValue = draft.code_ts ?? specStr("code");
  const setDebugging = onDebuggingChange;
  // The debugger steps the SAVED, transpiled JS (`spec.code`) — its line numbers, not
  // the TS source's. Gate on a saved body + a persisted query id.
  const compiledCode = specStr("code");
  const canDebug = (kind === "logic" || kind === "js") && !!queryId && !!compiledCode;

  // Logic / Compute → the code editor IS the surface (fills the pane).
  if (kind === "js" || kind === "logic") {
    if (debugging && canDebug) {
      return (
        <QueryDebugPanel
          appId={appId}
          queryId={queryId}
          code={compiledCode}
          codeTs={codeValue}
          input={debugInput}
          onClose={() => setDebugging(false)}
        />
      );
    }
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className={labelCls}>
            {kind === "logic" ? "Codice Logic · TypeScript (scrive + valida)" : "Codice Compute · TypeScript (sola lettura)"}
          </span>
          <button
            className="flex items-center gap-1 rounded border border-border px-2 py-0.5 text-[11px] text-foreground hover:bg-elevated disabled:opacity-40"
            title={canDebug ? "Debug con breakpoint (dry-run)" : "Salva la query per attivare il debug"}
            disabled={!canDebug}
            onClick={() => setDebugging(true)}
          >
            <Bug className="h-3.5 w-3.5 text-danger" /> Debug
          </button>
        </div>
        <CodeField
          fill
          label={kind === "logic" ? "Codice Logic" : "Codice Compute"}
          // The editor edits the TS SOURCE (`code_ts`); on save it's transpiled into
          // `spec.code` (what QuickJS runs). Legacy queries have no `code_ts` yet → fall
          // back to the stored JS so an existing body opens for editing (ADR-0054 S2).
          value={codeValue}
          onChange={(code) => onPatch({ code_ts: code })}
          ambientDts={logicDts}
          // A `handler(ctx, input): Output` body runs full Monaco TypeScript checking;
          // a legacy raw body (top-level `return`) stays in lenient JS mode.
          language={looksLikeHandler(codeValue) ? "typescript" : "javascript"}
          onDiagnostics={onCodeDiagnostics}
          placeholder={
            kind === "logic"
              ? "function handler(ctx: TrayCtx, input: Input): Output {\n  // const d = ctx.db.query('deals').rows[0];\n  // if (!d) ctx.fail('non trovato');\n  return { ok: true };\n}"
              : "function handler(ctx: TrayCtx, input: Input): Output {\n  // return ctx.db.query('deals', { page_size: 200 }).rows.length;\n}"
          }
        />
      </div>
    );
  }

  // Non-code kinds → a compact, roomy source-config card.
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      {kind === "table" && (
        <div className="flex flex-col gap-3">
          <label className="flex max-w-md flex-col gap-1">
            <span className={labelCls}>Tabella</span>
            <select className={inputCls} value={specStr("table_id")} disabled={!canWrite} onChange={(e) => onPatchSpec({ table_id: e.target.value })}>
              <option value="">— seleziona —</option>
              {tables.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          {/* ADR-0055 S4 — the VISUAL boolean-filter builder (the graphical twin of
              `ctx.db.query('deals', { filters })`); emits `spec.filters` (the tree). */}
          {specStr("table_id") && (
            <div className="flex max-w-2xl flex-col gap-1.5">
              <span className={labelCls}>Filtro righe (facoltativo)</span>
              <FilterTreeBuilder
                key={`${queryId}:${specStr("table_id")}`}
                columns={columnsByTable[specStr("table_id")] ?? []}
                value={draft.spec.filters}
                onChange={(filters: WireNode[]) => onPatchSpec({ filters })}
                disabled={!canWrite}
              />
            </div>
          )}
        </div>
      )}
      {kind === "dataset" && (
        <label className="flex max-w-md flex-col gap-1">
          <span className={labelCls}>Proiezione</span>
          <select className={inputCls} value={specStr("dataset_id")} disabled={!canWrite} onChange={(e) => onPatchSpec({ dataset_id: e.target.value })}>
            <option value="">— seleziona —</option>
            {datasets.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </label>
      )}
      {kind === "http" && (
        <div className="flex max-w-2xl flex-col gap-1">
          <span className={labelCls}>Richiesta HTTP</span>
          <div className="flex items-center gap-1.5">
            <select className={`${fieldCls} w-24`} value={specStr("method") || "GET"} disabled={!canWrite} onChange={(e) => onPatchSpec({ method: e.target.value })}>
              {["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <input
              className={`${fieldCls} min-w-0 flex-1 font-mono text-[11px]`}
              placeholder="https://api.example.com/items"
              value={specStr("url")}
              disabled={!canWrite}
              onChange={(e) => onPatchSpec({ url: e.target.value })}
            />
          </div>
        </div>
      )}
      {kind === "static" && (
        <label className="flex min-h-0 flex-1 flex-col gap-1">
          <span className={labelCls}>Valore (JSON)</span>
          <textarea
            className={`${inputCls} min-h-0 flex-1 resize-none font-mono`}
            disabled={!canWrite}
            value={typeof draft.spec.value === "string" ? draft.spec.value : JSON.stringify(draft.spec.value ?? null, null, 2)}
            onChange={(e) => {
              try {
                onPatchSpec({ value: JSON.parse(e.target.value) });
              } catch {
                onPatchSpec({ value: e.target.value });
              }
            }}
          />
        </label>
      )}
    </div>
  );
}

/** Right-rail guided test-run: a form-per-declared-input (coerced per type) or a
 *  free-text JSON box when no contract is declared, plus Esegui + the result. */
function TestRunPanel({
  params,
  runValues,
  setRunValues,
  runInput,
  setRunInput,
  runResult,
  running,
  onRun,
}: {
  params: QueryParam[];
  runValues: Record<string, string>;
  setRunValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  runInput: string;
  setRunInput: (s: string) => void;
  runResult: string | null;
  running: boolean;
  onRun: () => void;
}) {
  return (
    <div className="rounded-md border border-border/60">
      <div className="flex items-center justify-between px-2.5 py-2">
        <span className={labelCls}>Prova</span>
        <button
          type="button"
          disabled={running}
          onClick={onRun}
          className="flex items-center gap-1 rounded-md border border-accent/40 bg-accent/10 px-2.5 py-1 text-[11px] font-medium text-accent hover:bg-accent/20 disabled:opacity-50"
        >
          <Play className="h-3 w-3" strokeWidth={1.75} /> {running ? "Esecuzione…" : "Esegui"}
        </button>
      </div>
      <div className="flex flex-col gap-1.5 border-t border-border/60 p-2.5">
        {params.length > 0 ? (
          params.map((p) => (
            <label key={p.name} className="flex items-center gap-2">
              <span className="w-28 shrink-0 truncate font-mono text-[11px]" title={p.description ?? p.name}>
                {p.name}
                {p.required && <span className="text-danger">*</span>}
              </span>
              {p.type === "boolean" ? (
                <select
                  className={`${fieldCls} flex-1`}
                  value={runValues[p.name] ?? "false"}
                  onChange={(e) => setRunValues((v) => ({ ...v, [p.name]: e.target.value }))}
                >
                  <option value="false">false</option>
                  <option value="true">true</option>
                </select>
              ) : (
                <input
                  className={`${fieldCls} min-w-0 flex-1 font-mono`}
                  placeholder={p.type === "object" || p.type === "array" ? "JSON…" : p.type}
                  value={runValues[p.name] ?? ""}
                  onChange={(e) => setRunValues((v) => ({ ...v, [p.name]: e.target.value }))}
                />
              )}
            </label>
          ))
        ) : (
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] text-muted-foreground/70">Input JSON (ctx.input)</span>
            <textarea
              className={`${inputCls} font-mono`}
              rows={2}
              placeholder='{ "deal_id": "…" }'
              value={runInput}
              onChange={(e) => setRunInput(e.target.value)}
            />
          </label>
        )}
        {runResult !== null && (
          <div className="mt-1">
            <div className={labelCls}>Risultato</div>
            <pre className="mt-0.5 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded bg-muted/40 p-2 font-mono text-[10px]">
              {runResult}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

/** Collapsible secondary settings: description, trigger + cron, and the REST-
 *  exposure hint. Kept out of the way so the code + inputs stay central. */
function SettingsPanel({
  draft,
  canWrite,
  onPatch,
}: {
  draft: Draft;
  canWrite: boolean;
  onPatch: (p: Partial<Draft>) => void;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="rounded-md border border-border/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 px-2.5 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 hover:text-foreground"
      >
        <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} strokeWidth={2} />
        Impostazioni
        {draft.trigger === "schedule" && <span className="ml-auto rounded bg-muted px-1.5 py-0.5 text-[9px] normal-case tracking-normal">cron</span>}
      </button>
      {open && (
        <div className="flex flex-col gap-2 border-t border-border/60 p-2.5">
          <label className="flex flex-col gap-0.5">
            <span className={labelCls}>Descrizione</span>
            <input className={inputCls} value={draft.description} disabled={!canWrite} onChange={(e) => onPatch({ description: e.target.value })} />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className={labelCls}>Trigger</span>
            <select className={inputCls} value={draft.trigger} disabled={!canWrite} onChange={(e) => onPatch({ trigger: e.target.value as AppQueryTrigger })}>
              <option value="manual">Manuale (invocata)</option>
              <option value="schedule">Pianificata (cron)</option>
            </select>
          </label>
          {draft.trigger === "schedule" && (
            <label className="flex flex-col gap-0.5">
              <span className={labelCls}>Cron</span>
              <input className={`${inputCls} font-mono`} placeholder="0 * * * *" value={draft.schedule_cron} disabled={!canWrite} onChange={(e) => onPatch({ schedule_cron: e.target.value })} />
            </label>
          )}
          <p className="text-[10px] text-muted-foreground/70">
            Per esporre questa Query come API REST, aggiungila come endpoint
            nell'interfaccia <strong>REST</strong> dell'app (registro unico di esposizione).
          </p>
        </div>
      )}
    </div>
  );
}

/** The declared INPUT contract (ADR-0054): the params a caller (REST / button /
 *  page binding) may pass as `ctx.input`. Validated server-side on run → 422; it
 *  also drives the guided test-run form + the REST OpenAPI parameters. */
function ContractEditor({
  params,
  canWrite,
  onChange,
}: {
  params: QueryParam[];
  canWrite: boolean;
  onChange: (params: QueryParam[]) => void;
}) {
  const patchAt = (i: number, p: Partial<QueryParam>) =>
    onChange(params.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const removeAt = (i: number) => onChange(params.filter((_, j) => j !== i));
  const add = () => {
    const used = new Set(params.map((p) => p.name));
    let n = params.length + 1;
    while (used.has(`param${n}`)) n += 1;
    onChange([...params, { name: `param${n}`, type: "string", required: false, location: "query" }]);
  };

  const dupNames = new Set(
    params.map((p) => p.name).filter((n, i, a) => n && a.indexOf(n) !== i),
  );

  return (
    <div className="flex flex-col gap-1.5 border-t border-border/50 pt-2.5">
      <div className="flex items-center justify-between">
        <span className={labelCls}>Contratto · input</span>
        {canWrite && (
          <button
            type="button"
            onClick={add}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
          >
            <Plus className="h-3 w-3" strokeWidth={1.5} /> Parametro
          </button>
        )}
      </div>
      {params.length === 0 ? (
        <p className="text-[10px] text-muted-foreground/70">
          Nessun input dichiarato — la Query accetta qualsiasi payload.
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {params.map((p, i) => {
            const badName = !!p.name && (!_PARAM_NAME_RE.test(p.name) || dupNames.has(p.name));
            return (
              <div key={i} className="flex items-center gap-1">
                <input
                  className={`${fieldCls} w-28 min-w-0 font-mono ${badName ? "border-danger" : ""}`}
                  placeholder="nome"
                  value={p.name}
                  disabled={!canWrite}
                  onChange={(e) => patchAt(i, { name: e.target.value })}
                />
                <select
                  className={`${fieldCls} w-24`}
                  value={p.type}
                  disabled={!canWrite}
                  onChange={(e) => patchAt(i, { type: e.target.value as QueryParamType })}
                >
                  {PARAM_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <select
                  className={`${fieldCls} w-20`}
                  value={p.location}
                  disabled={!canWrite}
                  onChange={(e) => patchAt(i, { location: e.target.value as QueryParamLocation })}
                >
                  {PARAM_LOCATIONS.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
                <label className="flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground" title="Obbligatorio">
                  <input
                    type="checkbox"
                    checked={p.required}
                    disabled={!canWrite}
                    onChange={(e) => patchAt(i, { required: e.target.checked })}
                  />
                  obb.
                </label>
                <input
                  className={`${fieldCls} min-w-0 flex-1`}
                  placeholder="descrizione"
                  value={p.description ?? ""}
                  disabled={!canWrite}
                  onChange={(e) => patchAt(i, { description: e.target.value || null })}
                />
                {canWrite && (
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    className="shrink-0 rounded p-0.5 text-danger hover:bg-danger/10"
                    title="Rimuovi"
                  >
                    <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

